(ns manage.adset-decision-tree-page.view.predicate-editor.converter
  (:require ["moment-timezone$default" :as moment]
            [clojure.set :as set]
            [clojure.string :as str]
            [common.content-function.schemas :as schema-util]
            [common.util :as util]
            [lemonpi.content-function.shared.macro
             :refer [parse-macro-expr serialize-macro]]
            [manage.content-configuration-page.predicate.db :refer [operators]]
            [mate.core :as mc]))

;; This should probably come from the schema endpoints
(def trigger->predicate-expression-details
  {:temperature-of-today {:unit "\u00B0C"}
   :temperature-of-tomorrow {:unit "\u00B0C"}})

(def operator-lookup (mc/index-by :key operators))

(def empty-predicate
  #js {:trigger nil
       :operator nil
       :operands nil})

(defn get-property-operators [property]
  (cond->> operators
    property (filter (fn [{:keys [supported-types]}]
                       (contains? supported-types (keyword (:type property)))))))

(defn trigger->selector [trigger]
  (when-not (str/blank? trigger)
    [(keyword trigger)]))

(defn schema-output->cf-schema [output]
  {:data {:out output}})

(defn get-predicate-details [schema {:keys [trigger operator]}]
  (let [{:keys [value-type-modifier]} (get operator-lookup (keyword operator))
        property (schema-util/find-property (trigger->selector (keyword trigger)) schema)]
    (assoc property :value-type-modifier value-type-modifier)))

(defn ->cf-operands [{:keys [value-type-modifier] property-type :type} operands]
  (let [unparse-operand
        (fn [operand]
          (case property-type
            "time" (when (moment/isMoment operand) (util/moment->timemap operand))
            "timestamp" (when (moment/isMoment operand)
                          {:timestamp (.toISOString operand)})
            "integer" (when (string? operand)
                        (parse-long operand))
            "bool" true
            operand))]
    (case value-type-modifier
      :range (some-> operands
                     (dissoc :value)
                     (update :start unparse-operand)
                     (update :end unparse-operand))
      (unparse-operand (:value operands)))))

;; see similar in manage.content-configuration-page.content-configuration.export
(defn- get-predicate-vector
  [schema-alias schema {:keys [trigger operator operands] :as predicate-item}]
  (let [macro (serialize-macro {:alias schema-alias
                                :selector (trigger->selector (keyword trigger))})
        operands (->cf-operands (get-predicate-details schema predicate-item) operands)]
    (when (and trigger operator)
      (cond-> [operator macro]
        (some? operands) (conj operands)))))

(defn ->cf-predicates [schema-alias schema predicates]
  (let [cf-predicates (keep (partial get-predicate-vector schema-alias schema) predicates)]
    (cond
      (zero? (count cf-predicates)) nil
      (= (count cf-predicates) 1) (first cf-predicates)
      :else (into ["and"] cf-predicates))))

(defn <-cf-operands  [{:keys [value-type-modifier] property-type :type} operands]
  (let [parse-operand
        (fn [operand]
          (case property-type
            "time" (when (some? operand) (util/timemap->moment operand))
            "timestamp" (when-let [timestamp (:timestamp operand)]
                          (moment timestamp))
            "integer" (str operand)
            "bool" true
            operand))]
    (case value-type-modifier
      :range (-> operands
                 (update :start parse-operand)
                 (update :end parse-operand))
      {:value (parse-operand operands)})))

(defn get-schema-alias-from-predicate [ext-predicates]
  (let [first-predicate (if (= (first ext-predicates) "and")
                          (second ext-predicates)
                          ext-predicates)]
    (-> first-predicate second parse-macro-expr first :alias)))

;; see similar in manage.content-configuration-page.content-configuration.import
(defn- parse-predicate-vector [schema [operator macro value]]
  (let [{[trigger] :selector} (first (parse-macro-expr macro))
        predicate-item {:trigger (when trigger (name trigger))
                        :operator operator}]
    (cond-> predicate-item
      (some? value) (assoc :operands (<-cf-operands
                                      (get-predicate-details schema predicate-item)
                                      value)))))

(defn <-cf-predicates [schema ext-predicates]
  (cond
    ;; already parsed
    (map? (first ext-predicates)) ext-predicates

    (or (nil? ext-predicates) (true? ext-predicates)) [{}]

    (and (sequential? ext-predicates) (= (first ext-predicates) "and"))
    (map (partial parse-predicate-vector schema) (rest ext-predicates))

    :else [(parse-predicate-vector schema ext-predicates)]))

(defn ->form [predicate-items {:keys [output]}]
  (<-cf-predicates (schema-output->cf-schema output) predicate-items))

(defn <-form [predicate-items {:keys [output alias]}]
  (when (and output alias)
    (->cf-predicates alias (schema-output->cf-schema output) predicate-items)))

(defn ->validation [predicate-items schema]
  (into {} (map-indexed vector (->form predicate-items schema))))

(defn get-operands-text
  ([schema predicate-details predicate-item]
   (get-operands-text schema predicate-details predicate-item nil))
  ([schema {property-type :type} {:keys [operands trigger]} parent-node]
   (let [selector (trigger->selector trigger)
         value-lookup (->> (schema-util/find-values selector schema nil)
                           (map (juxt :value :label))
                           (into {}))
         {:keys [unit]} (get-in trigger->predicate-expression-details selector)
         ;; Always use timezone - either from config or browser
         timezone (if parent-node
                    (or (get-in parent-node [:config :meta :timezone])
                        (get-in parent-node [:config :tiktok :timezone])
                        ;; Fallback to browser timezone
                        (try
                          (.guess (.-tz moment))
                          (catch :default _ "UTC")))
                    "UTC")
         ;; "time" operands are wall-clock times (hour/minute) built by
         ;; util/timemap->moment, which doesn't carry the moment-timezone plugin,
         ;; so only actual timestamps get converted to the timezone.
         format-moment (fn [m]
                         (case property-type
                           "time" (.format m "LT")
                           "timestamp" (let [m-in-tz (.tz m timezone)]
                                         (str (.format m-in-tz "L LT") " (" (.format m-in-tz "z") ")"))
                           m))
         {:keys [value end start]}
         (update-vals operands #(cond-> %
                                  (moment/isMoment %) format-moment
                                  unit (str unit)))]
     (if value
       (cond->> value
         (not (sequential? value)) (vector)
         (seq value-lookup) (map #(get value-lookup %))
         true (str/join ", "))
       (str start " and " end)))))

(defn get-predicate-item-summary
  ([schema index predicate-item]
   (get-predicate-item-summary schema nil index predicate-item))
  ([schema parent-node index {:keys [operator trigger operands] :as predicate-item}]
   (let [{property-type :type
          trigger-label :label
          :keys [value-type-modifier] :as predicate-details}
         (get-predicate-details schema predicate-item)
         has-no-operands (or (= property-type "bool") (= value-type-modifier :nil))]
     (when (and operator trigger (or has-no-operands operands))
       (str (when (pos? index) "And ")

            (or trigger-label trigger) " "

            (cond-> (get-in operator-lookup [(keyword operator) :label])
              (= property-type "bool") (str " supported")) " "

            (when-not has-no-operands
              (get-operands-text schema predicate-details predicate-item parent-node)))))))

(defn get-predicate-summaries
  ([output predicate]
   (get-predicate-summaries output predicate nil))
  ([output predicate parent-node]
   (let [predicate-items (<-cf-predicates (schema-output->cf-schema output) predicate)
         schema (schema-output->cf-schema output)]
     (keep-indexed (partial get-predicate-item-summary schema parent-node) predicate-items))))

(def string-like-operators (into #{}
                                 (comp
                                  (filter (fn [{:keys [supported-types]}]
                                            (set/intersection #{:string :enum :option} supported-types)))
                                  (map :key))
                                 operators))

(defn- predicate->string-value [[operator _ value]]
  (when (string-like-operators (keyword operator))
    (let [{:keys [value-type-modifier]} (get operator-lookup (keyword operator))]
      (case value-type-modifier
        nil [value]
        :list value
        nil))))

(defn predicates->string-value [predicates]
  (when-some [simple-values (if (= "and" (first predicates))
                              (->> (rest predicates)
                                   (mapcat predicate->string-value))
                              (predicate->string-value predicates))]
    (str/join ", " simple-values)))

(defn adjust-schema-to-property
  [{:keys [allowed-schema-keys] :as _property} schema]
  (cond-> schema
    (some? allowed-schema-keys)
    (update :output select-keys allowed-schema-keys)))
